import React, { useState, useEffect, useMemo } from 'react';
import {
    MdStar,
    MdRateReview,
    MdContentCut,
    MdEvent,
    MdWarning,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdPerson
} from 'react-icons/md';

import { ratingsAPI, servicesAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YILDIZ GÖSTERİM BİLEŞENİ ---
const StarRating = ({ rating }) => {
    const rounded = Math.round(Number(rating) || 0);
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <MdStar
                    key={star}
                    className={`text-xl md:text-2xl ${
                        star <= rounded ? "text-amber-400 drop-shadow-sm" : "text-gray-200 dark:text-navy-700"
                    }`}
                />
            ))}
        </div>
    );
};

// --- SKELETON LOADER ---
const ReviewsSkeleton = () => (
    <div className="flex flex-col gap-5 animate-pulse mt-4">
        <div className="h-28 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        {[...Array(4)].map((_, i) => (
            <div key={`skel-${i}`} className="flex flex-col p-6 rounded-[20px] bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col gap-2 w-1/3">
                        <div className="h-5 w-full bg-gray-200 dark:bg-navy-700 rounded" />
                        <div className="h-4 w-1/2 bg-gray-200 dark:bg-navy-700 rounded" />
                    </div>
                    <div className="h-6 w-24 bg-gray-200 dark:bg-navy-700 rounded-full" />
                </div>
                <div className="h-16 w-full bg-gray-200 dark:bg-navy-700 rounded-xl" />
            </div>
        ))}
    </div>
);

// --- ANA BİLEŞEN ---
const StaffReviews = () => {
    const { user, logout } = useAuth();

    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [totalRatings, setTotalRatings] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sayfalama (Pagination) Stateleri
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    const handleAuthError = () => {
        logout();
    };

    // --- API VERİ ÇEKME VE EŞLEŞTİRME (HATA GİDERİLDİ, MANTIK BİREBİR) ---
    const fetchReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const staffId = user?.id;
            if (!staffId) throw new Error('Personel bilgisi bulunamadı');

            // HATA DÜZELTİLDİ: getStaffRatings -> getByStaff
            const [reviewsResponse, servicesResponse] = await Promise.all([
                ratingsAPI.getByStaff(staffId),
                servicesAPI.getAll(),
            ]);

            if (!reviewsResponse?.success || !servicesResponse?.success) {
                throw new Error('Veriler yüklenirken bir hata oluştu.');
            }

            // Servisleri Map'e atma (Orijinal yapı)
            const serviceMap = new Map();
            if (Array.isArray(servicesResponse.services)) {
                servicesResponse.services.forEach((service) => {
                    serviceMap.set(service.id, service.name);
                });
            }

            // Verileri formatlama ve sıralama
            const formatted = (reviewsResponse.ratings || [])
                .map((rating) => {
                    const serviceId = rating?.appointment?.service_id;
                    const createdAt = rating?.created_at;
                    return {
                        id: rating.id,
                        customerName: 'Anonim Müşteri',
                        dateISO: createdAt ? new Date(createdAt).toISOString() : null,
                        date: createdAt
                            ? new Date(createdAt).toLocaleDateString('tr-TR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })
                            : '',
                        serviceName: serviceMap.get(serviceId) || 'Hizmet bilgisi yok',
                        rating: Number(rating?.response_rating ?? rating?.rating ?? 0),
                        comment: rating?.comment || null,
                    };
                })
                .sort((a, b) => {
                    return (b.dateISO ? Date.parse(b.dateISO) : 0) - (a.dateISO ? Date.parse(a.dateISO) : 0);
                });

            setReviews(formatted);
            setAverageRating(Number(reviewsResponse.average_rating || 0));
            setTotalRatings(Number(reviewsResponse.total_ratings || 0));
            setCurrentPage(1); // Veri gelince 1. sayfaya dön
        } catch (err) {
            console.error('Değerlendirmeler yüklenirken hata:', err);
            const msg = String(err?.message || '');
            if (msg.includes('401') || msg.includes('Unauthorized')) {
                handleAuthError();
                return;
            }
            setError(msg || 'Değerlendirmeler yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchReviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // --- SAYFALAMA HESAPLARI (PAGINATION) ---
    const totalPages = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));
    const currentReviews = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return reviews.slice(start, start + PAGE_SIZE);
    }, [reviews, currentPage]);

    // --- RENDER ---
    if (loading) {
        return (
            <div className="mt-5 pb-20 w-full">
                <ReviewsSkeleton />
            </div>
        );
    }

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30 flex-none">
                        <MdStar className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Değerlendirmelerim</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Müşterilerinizin hakkınızda yaptığı anonim yorumlar.
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2 shadow-sm">
                    <MdWarning className="text-xl flex-none" /> {error}
                </div>
            )}

            {/* 2. ORTALAMA PUAN KARTI (Lüks Tasarım) */}
            <div className="flex flex-col md:flex-row items-center justify-between p-6 md:p-8 bg-gradient-to-br from-brand-500 to-brand-700 dark:from-navy-700 dark:to-navy-800 rounded-[20px] shadow-lg shadow-brand-500/20 text-white relative overflow-hidden">
                <div className="absolute -right-10 -top-10 opacity-10">
                    <MdRateReview className="text-[200px]" />
                </div>
                <div className="relative z-10 flex flex-col items-center md:items-start text-center md:text-left mb-4 md:mb-0">
                    <h3 className="text-2xl font-black mb-1">Genel Puanınız</h3>
                    <p className="text-sm font-medium text-white/80">Toplam {totalRatings} değerlendirme baz alınmıştır.</p>
                </div>
                <div className="relative z-10 flex flex-col items-center bg-white/10 backdrop-blur-md px-8 py-4 rounded-2xl border border-white/20">
                    <h1 className="text-5xl font-black text-amber-400 mb-2 drop-shadow-md">{averageRating.toFixed(1)}</h1>
                    <StarRating rating={averageRating} />
                </div>
            </div>

            {/* 3. DEĞERLENDİRME LİSTESİ */}
            <div className="flex flex-col gap-5">
                {reviews.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 opacity-70">
                        <MdStar className="text-7xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Henüz yorum yapılmamış.</h5>
                        <p className="text-sm font-medium text-gray-500 text-center max-w-md">
                            Tamamladığınız randevuların ardından müşterilerin yaptığı değerlendirmeler burada listelenecektir.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-5">
                        {currentReviews.map((review) => (
                            <div
                                key={review.id}
                                className="flex flex-col md:flex-row p-6 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 hover:shadow-md transition-shadow duration-300 gap-6"
                            >

                                {/* Sol: Müşteri ve Yıldız Bilgisi */}
                                <div className="flex flex-col gap-3 w-full md:w-1/3 md:border-r border-gray-100 dark:border-white/10 md:pr-6">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/10 flex items-center justify-center font-bold text-lg flex-none">
                                            <MdPerson />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-base font-bold text-navy-700 dark:text-white">{review.customerName}</span>
                                            <span className="text-xs font-medium text-gray-400">{review.date}</span>
                                        </div>
                                    </div>
                                    <div className="mt-auto">
                                        <StarRating rating={review.rating} />
                                    </div>
                                </div>

                                {/* Sağ: Hizmet ve Yorum */}
                                <div className="flex flex-col gap-3 w-full md:w-2/3">
                                    <div className="flex items-center gap-2 text-sm font-bold text-brand-500 dark:text-brand-400 bg-brand-50 dark:bg-brand-400/10 self-start px-3 py-1.5 rounded-lg">
                                        <MdContentCut /> {review.serviceName}
                                    </div>

                                    {review.comment ? (
                                        <div className="relative mt-2 p-4 rounded-xl bg-gray-50 dark:bg-navy-900/40 border border-gray-100 dark:border-white/5">
                                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-l-xl" />
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-300 italic leading-relaxed pl-2">
                                                "{review.comment}"
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-400 italic mt-2">
                                            (Müşteri yazılı yorum bırakmadı)
                                        </p>
                                    )}
                                </div>

                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 4. SAYFALAMA (PAGINATION) */}
            {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between p-6 mt-2 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 gap-4">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            <span className="font-bold text-navy-700 dark:text-white">{reviews.length}</span> değerlendirmeden <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, reviews.length)}</span> gösteriliyor.
          </span>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-500 disabled:opacity-40 hover:bg-gray-100 dark:border-white/5 dark:bg-navy-900 dark:hover:bg-navy-700 transition-all active:scale-95"
                        >
                            <MdKeyboardArrowLeft className="text-2xl" />
                        </button>

                        <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                            {currentPage}
                        </div>

                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-500 disabled:opacity-40 hover:bg-gray-100 dark:border-white/5 dark:bg-navy-900 dark:hover:bg-navy-700 transition-all active:scale-95"
                        >
                            <MdKeyboardArrowRight className="text-2xl" />
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default StaffReviews;